import { Outlet, Navigate, useRouterState } from "@tanstack/react-router";
import { useActiveUser } from "../../hooks/useActiveUser";
import { getAppHost, audienceAllowedOnHost } from "../../lib/appHost";
import { PortalSidebar } from "./portal-sidebar";
import { PortalNotificationBell } from "./portal-notification-bell";
import { PathBreadcrumb, labelFor } from "./path-breadcrumb";
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import { Separator } from "@/components/ui/separator";

/** Portal (client + B2C subscriber) section labels for the header breadcrumb. */
const PORTAL_LABELS: Record<string, string> = {
  dashboard: "Dashboard",
  subscriptions: "My Services",
  marketplace: "Marketplace",
  recommendations: "Recommendations",
  invoices: "Invoices",
  team: "Team",
  profile: "Profile",
};

/** Shell for all /portal/* routes — shadcn SidebarProvider/Inset pattern, matching the
 *  provider console for a consistent left-sidebar experience (was a top NavigationMenu). */
export function ClientLayout(): JSX.Element {
  const {
    isLoading,
    isError,
    isSignedIn,
    userType,
    appAudience,
    platformName,
  } = useActiveUser();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const section = pathname.split("/").filter(Boolean)[1] ?? "dashboard";

  // ENH-004: userType is the ACTIVE tenant's role (auth.me), not global Clerk metadata.
  if (isLoading)
    return <div className="p-10 text-muted-foreground">Loading…</div>;
  if (!isSignedIn || isError) return <Navigate to="/login" />;
  if (userType?.startsWith("provider_"))
    return <Navigate to="/provider/dashboard" />;
  // Host-as-trust-boundary (AUTH-06/08, GAP-025): a B2B client on the B2C host (or
  // vice-versa) deep-linking here is bounced to "/", where RootRedirect shows the
  // WrongApp screen. Providers are 'both' and pass on either host.
  if (appAudience && !audienceAllowedOnHost(appAudience, getAppHost())) {
    return <Navigate to="/" />;
  }

  return (
    <SidebarProvider>
      <PortalSidebar />
      <SidebarInset>
        <header className="flex h-16 shrink-0 items-center gap-2 border-b px-4">
          <SidebarTrigger className="-ml-1" />
          <Separator
            orientation="vertical"
            className="mr-2 data-[orientation=vertical]:h-4"
          />
          <PathBreadcrumb
            rootLabel={platformName}
            sectionLabel={labelFor(PORTAL_LABELS, section)}
          />
          <div className="ml-auto flex items-center gap-1">
            <PortalNotificationBell />
          </div>
        </header>
        <main className="flex-1 overflow-auto p-4 md:p-6 lg:p-8">
          <Outlet />
        </main>
      </SidebarInset>
    </SidebarProvider>
  );
}
